import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { vendorApi } from "../../api/api";
import { useLocation } from "react-router-dom";
import GradientButton from "../common/GradiantButton";
import { useNavigate } from "react-router-dom";

const AddPlot = () => {
  const userId = useSelector((state) => state.user.userId);
  const location = useLocation();
  const { data } = location.state;
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    projectId: data.projectId,
    projectName: data.projectName,
    plotName: "",
    address: data.address,
    city: data.city,
    facing: "",
    sizeInSqft: "",
    sqftPrice: "",
  });

  const [image, setImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: false })); // clear error when user types
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
    }
  };

  const handleSubmit = async () => {
    const { plotName, sizeInSqft, sqftPrice, projectName, address, city, facing } = form;

    let newErrors = {};
    if (!plotName) newErrors.plotName = true;
    if (!sizeInSqft) newErrors.sizeInSqft = true;
    if (!sqftPrice) newErrors.sqftPrice = true;
    if (!projectName) newErrors.projectName = true;
    if (!address) newErrors.address = true;
    if (!city) newErrors.city = true;
    if (!facing) newErrors.facing = true;
    // if (!image) newErrors.iconUrl = true;

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields",
        type: "error",
      });
      return;
    }

    const formData = new FormData();
    formData.append("VendorId", userId);
    formData.append("ProjectId", form.projectId);
    formData.append("ProjectName", form.projectName);
    formData.append("PropertyName", form.plotName);
    formData.append("Address", form.address);
    formData.append("City", form.city);
    formData.append("Facing", form.facing);
    formData.append("SizeInSqFt", form.sizeInSqft);
    formData.append("SqFtPrice", form.sqftPrice);
    if (image) {
      formData.append("iconUrl", image);
    }


    setLoading(true);

    try {
      const response = await vendorApi.addPlot(formData);

      if (response?.status === 201) {
        setSnackbar({
          open: true,
          message: response.data.message || "Plot added successfully!",
          type: "success",
        });
        setForm((prev) => ({
          ...prev,
          plotName: "",
          facing: "",
          sizeInSqft: "",
          sqftPrice: "",
        }));
        setImage(null);
        setErrors({});
        setTimeout(() => {
          navigate(-1);
        }, 2000); // 2000 ms = 2 seconds

      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to add plot", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 600,
        mx: "auto",
        mt: 4,
        px: 2,
        py: 3,
        boxShadow: 3,
        borderRadius: 2,
        backgroundColor: "#fff",
      }}
    >
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Add Plot to {data.projectName}
      </Typography>

      <TextField
        fullWidth
        label="Plot Name"
        variant="outlined"
        value={form.plotName}
        onChange={(e) => handleChange("plotName", e.target.value)}
        error={!!errors.plotName}
        helperText={errors.plotName && "Plot Name is required"}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Address"
        variant="outlined"
        value={form.address}
        InputProps={{ readOnly: true }}
        error={!!errors.address}
        helperText={errors.address && "Address is required"}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="City"
        variant="outlined"
        value={form.city}
        InputProps={{ readOnly: true }}
        error={!!errors.city}
        helperText={errors.city && "City is required"}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Facing"
        variant="outlined"
        value={form.facing}
        onChange={(e) => handleChange("facing", e.target.value)}
        error={!!errors.facing}
        helperText={errors.facing && "Facing is required"}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Size in SqFt"
        type="number"
        variant="outlined"
        value={form.sizeInSqft}
        onChange={(e) => handleChange("sizeInSqft", e.target.value)}
        error={!!errors.sizeInSqft}
        helperText={errors.sizeInSqft && "Size in SqFt is required"}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="SqFt Price"
        type="number"
        variant="outlined"
        value={form.sqftPrice}
        onChange={(e) => handleChange("sqftPrice", e.target.value)}
        error={!!errors.sqftPrice}
        helperText={errors.sqftPrice && "SqFt Price is required"}
        sx={{ mb: 2 }}
      />

      <Button
        variant="contained"
        component="label"
        fullWidth
        sx={{ mb: 2 }}
        color={errors.iconUrl ? "error" : "primary"}
      >
        Upload Icon Image
        <input
          type="file"
          accept="image/*"
          hidden
          onChange={handleImageChange}
        />
      </Button>


      {image && (
        <Box sx={{ mb: 2 }}>
          <img
            src={URL.createObjectURL(image)}
            alt="Preview"
            style={{
              width: "100%",
              height: 200,
              objectFit: "cover",
              borderRadius: 8,
            }}
          />
        </Box>
      )}

      <GradientButton
        onClick={handleSubmit}
        text={
          loading ? <CircularProgress size={24} color="inherit" /> : "Add Plot"
        }
        width="100%"
      />

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddPlot;
